import { components } from 'admin/schema/docs/api/admin';
import _ from 'lodash';
import { authorityRules } from '../authorities';

export type User = components['schemas']['User'];
type PrivateMoney = NonNullable<User['private_moneys']>[number];

export default class AdminUser {
  private user: User;

  public get id() {
    return this.user.id;
  }

  public get name() {
    return this.user.name;
  }

  public get email() {
    return this.user.email;
  }

  public get role() {
    return this.user.role;
  }

  public get is_active() {
    return this.user.is_active;
  }

  public get organization() {
    return this.user.organization;
  }

  public get privateMoneys() {
    return this.user.private_moneys;
  }

  public get shops() {
    return this.user.shops;
  }

  // 発行体組織単位のオプション機能フラグ（GET /api/user の display_features_by_organization）。
  public get displayFeaturesByOrganization() {
    return this.user.display_features_by_organization ?? {};
  }

  constructor(user: User) {
    this.user = user;
  }

  isShopUser() {
    return !!this.role && ['SHOP-STAFF', 'SHOP-MANAGER'].indexOf(this.role) !== -1;
  }

  isOrgUser() {
    return !!this.role && ['ORGANIZATION-STAFF', 'ORGANIZATION-MANAGER'].indexOf(this.role) !== -1;
  }

  isServiceUser() {
    return !!this.role && ['SERVICE-STAFF', 'SERVICE-MANAGER'].indexOf(this.role) !== -1;
  }

  isManagerUser() {
    return !!this.role && ['ORGANIZATION-MANAGER', 'SERVICE-MANAGER'].indexOf(this.role) !== -1;
  }

  // XXX: maybe pocketchange money should be filtered by the server
  moneys() {
    return _.filter(this.privateMoneys, (p) => p.organization?.code !== 'pocketchange');
  }

  thirdPartyMoneys() {
    return _.filter(
      this.privateMoneys,
      (p) => p.type === 'third-party' && p.organization?.code !== 'pocketchange'
    );
  }

  issuedMoneys() {
    return _.filter(this.privateMoneys, (p) => p.organization?.code === this.organization?.code);
  }

  issuedThirdPartyMoneys() {
    return _.filter(
      this.privateMoneys,
      (p) => p.type === 'third-party' && p.organization?.code === this.organization?.code
    );
  }

  memberMoneys() {
    // ???: pokepayマネーの加盟店はいないのか？
    return _.filter(
      this.privateMoneys,
      (p) =>
        p.organization?.code !== 'pocketchange' && p.organization?.code !== this.organization?.code
    );
  }

  defaultMoney() {
    return _.head(this.issuedMoneys()) || _.head(this.memberMoneys()) || _.head(this.privateMoneys);
  }

  isAllowedTo(action: string, resourceName: string, resource = {}, privateMoneyUuid = '') {
    // XXX: PrivateMoneyに関係のないリソースはMemberとしての権限に依る
    const orgType = this.isIssuerOf(privateMoneyUuid) ? 'issuer' : 'member';
    const permission = `${resourceName}:${action}`;
    return authorityRules[orgType][permission](this, resource);
  }

  isIssuerOf(moneyId: string) {
    if (moneyId) {
      return (
        _.intersection(['third-party', 'own'], this.organization?.issuer_type).length > 0 &&
        _.some(this.issuedMoneys(), ['id', moneyId])
      );
    }
    return _.intersection(['third-party', 'own'], this.organization?.issuer_type).length > 0;
  }

  isIssuerOfMoney(privateMoney: PrivateMoney) {
    return this.organization?.code === privateMoney.organization?.code;
  }

  // 選択中マネーの発行体組織における display feature フラグを解決する。
  // money.organization.code を display_features_by_organization から引き、未設定は false。
  isDisplayFeatureEnabled(
    money: PrivateMoney | undefined,
    featureKey: keyof components['schemas']['DisplayFeatures']
  ): boolean {
    const code = money?.organization?.code;
    if (!code) return false;
    return this.displayFeaturesByOrganization[code]?.[featureKey] === true;
  }
}
